import {
  queryOptions,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { queryKeysUser } from '../queryKeys';

import { fetchMyUser } from './fetchMyUser';
import { type MyUser } from './types';
import { INTERNAL_ADMIN_PERMISSION_LEVELS } from './validations';

export const selectMyUserData = (data: MyUser) => {
  const fullNameFallback =
    data.email
      ?.split('@')[0]
      ?.split('.')
      ?.map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      ?.join(' ') ?? 'Admin User';
  const fullName =
    [data.firstName, data.lastName].filter(Boolean).join(' ').trim() ||
    fullNameFallback;

  return {
    ...data,
    fullName,
    hasAdminWriteAccess:
      data.permissionLevel === 'admin' ||
      data.permissionLevel === 'admin_mcp_write',
    isAdmin: INTERNAL_ADMIN_PERMISSION_LEVELS.some(
      (level) => level === data.permissionLevel,
    ),
    isExternalAdmin: data.permissionLevel === 'external_admin',
  };
};

/**
 * Generates the query options for the current user query.
 *
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getMyUserQueryOptions = <TData = MyUser>(
  options?: Omit<UseQueryOptions<MyUser, Error, TData>, 'queryKey' | 'queryFn'>,
) =>
  queryOptions<MyUser, Error, TData>({
    queryFn: async ({ signal }: QueryFunctionContext) => {
      const user = await fetchMyUser({ signal });
      if (!user) {
        throw new Error('Current user request returned no data');
      }
      return user;
    },
    queryKey: queryKeysUser.getMe(),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for fetching the current authenticated user's details.
 * Requires ADMIN access.
 *
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_my_user_vadmin_user_me_get
 */
export type MyUserSelected = ReturnType<typeof selectMyUserData>;

export const useQueryMyUser = (
  options?: Omit<
    UseQueryOptions<MyUser, Error, MyUserSelected>,
    'queryKey' | 'queryFn' | 'select'
  >,
) =>
  useQuery({
    select: selectMyUserData,
    ...getMyUserQueryOptions(options),
  });
